import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const KG_PER_LB = 0.45359237;
const LB_PER_KG = 1 / KG_PER_LB;

// "level" is a third, unitless option for machine-based work (air bike,
// spin bike resistance, stack machines). It doesn't convert to/from lb —
// the stored value IS the level number, always a whole integer.

/**
 * Pill-toggle weight input. Stores the user's chosen unit per-entry.
 *
 * Props:
 *   value      number in the *current display unit*; null/"" allowed
 *   unit       "lb" | "kg" | "level"
 *   onChange   (newValue, newUnit) => void  — fires on either field
 *   label      string (default "Weight")
 *   testId     string — applied to the <input>; toggle buttons get `${testId}-unit-lb|kg|level`
 *   placeholder optional
 *   disabled   bool
 *   step       string — default "0.5" for lb/kg, forced to "1" for level
 *   className  applied to the wrapper
 */
export default function WeightInput({
  value, unit, onChange,
  label = "Weight",
  testId,
  placeholder,
  disabled = false,
  step = "0.5",
  className = "",
}) {
  const u = unit === "kg" ? "kg" : unit === "level" ? "level" : "lb";
  const isLevel = u === "level";

  const handleValue = (e) => {
    const raw = e.target.value;
    if (raw === "") { onChange(null, u); return; }
    // Level values are always whole integers.
    const n = isLevel ? Math.max(0, Math.round(Number(raw))) : Number(raw);
    onChange(n, u);
  };

  const handleToggle = (newUnit) => {
    if (newUnit === u) return;
    // Any switch involving "level" resets the numeric field: level and
    // lb/kg live in different measurement domains and silently coercing
    // one into the other would be dangerous.
    if (newUnit === "level" || u === "level") {
      onChange(null, newUnit);
      return;
    }
    let converted = value;
    if (value != null && value !== "") {
      const n = Number(value);
      if (!Number.isNaN(n)) {
        converted = newUnit === "kg"
          ? Math.round(n * KG_PER_LB * 10) / 10
          : Math.round(n * LB_PER_KG * 10) / 10;
      }
    }
    onChange(converted, newUnit);
  };

  return (
    <div className={className}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Label className="hs-label">{label}</Label>
        <div
          className="inline-flex bg-muted2 rounded-lg p-0.5 border border-line/70 shadow-sm"
          role="radiogroup"
          aria-label={`${label} unit`}
        >
          {["lb", "kg", "level"].map((opt) => (
            <button
              key={opt}
              type="button"
              role="radio"
              aria-checked={u === opt}
              onClick={() => handleToggle(opt)}
              disabled={disabled}
              className={`min-w-[28px] px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.15em] transition-colors ${
                u === opt ? "bg-surface text-ink shadow-sm" : "text-ink-2 hover:text-ink"
              }`}
              data-testid={testId ? `${testId}-unit-${opt}` : `weight-unit-${opt}`}
              title={opt === "level" ? "Machine level (e.g., air bike resistance)" : opt}
            >
              {opt === "level" ? "L" : opt}
            </button>
          ))}
        </div>
      </div>
      <Input
        type="number"
        step={isLevel ? "1" : step}
        min={isLevel ? "0" : undefined}
        value={value ?? ""}
        onChange={handleValue}
        disabled={disabled}
        placeholder={isLevel ? "e.g., 5" : placeholder}
        className="mt-2 h-11 rounded-xl font-data"
        data-testid={testId}
      />
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────────

/** Convert a (value, unit) pair from a form into canonical storage. Level
 *  is stored as-is (unitless integer); lb/kg are canonicalised to lb. */
export function toCanonicalLb(value, unit) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  if (unit === "level") return Math.max(0, Math.round(n));
  if (unit === "kg") return Math.round(n * LB_PER_KG * 100) / 100;
  return Math.round(n * 100) / 100;
}

/** Convert canonical storage → value in the given display unit. Level
 *  passes through unchanged since there is nothing to convert. */
export function fromCanonicalLb(lb, unit) {
  if (lb == null || lb === "") return null;
  const n = Number(lb);
  if (Number.isNaN(n)) return null;
  if (unit === "level") return Math.max(0, Math.round(n));
  if (unit === "kg") return Math.round(n * KG_PER_LB * 10) / 10;
  return Math.round(n * 10) / 10;
}

/** Display: "185 lb", "84 kg", or "L5" — given canonical value + the unit
 *  the entry was recorded in. */
export function formatStoredWeight(lb, unit) {
  const v = fromCanonicalLb(lb, unit);
  if (v == null) return "";
  if (unit === "level") return `L${Math.round(v)}`;
  const txt = (Math.round(v * 10) % 10 === 0) ? String(Math.round(v)) : String(v);
  return `${txt} ${unit === "kg" ? "kg" : "lb"}`;
}
